import { useMemo, useState, useEffect } from 'react';

import type { WhiteboardToken } from '@confluence/whiteboard-common';
import { useSessionData } from '@confluence/session-data';
import { useTokenService } from '@confluence/whiteboard-common';

import { useInitialUrl } from './useInitialUrl';

interface Identity {
	accountId?: string | null;
	nickname?: string | null;
	picture?: string | null;
	email?: string | null;
	locale?: string | null;
}

export interface DatabaseBridgeDataProps {
	spaceKey?: string;
	contentId?: string;
	contentReferenceId?: string | null;
	spaceId?: string | null;
	spaceName?: string | null;
	title?: string | null;
	identity?: Identity | null;
	dataSecurityPolicyPageExport: boolean | null;
	refetch?: () => void;
	version?: string;
	isSmartLinkEmbed?: boolean;
}

export const useDatabaseBridgeData = ({
	spaceKey,
	contentId,
	contentReferenceId,
	spaceId,
	spaceName,
	title,
	identity,
	dataSecurityPolicyPageExport,
	refetch,
	version,
	isSmartLinkEmbed,
}: DatabaseBridgeDataProps) => {
	const [token, setToken] = useState<WhiteboardToken | undefined>(undefined);
	const { activationId, userId, cloudId } = useSessionData();
	const initialUrl = useInitialUrl({ version, isSmartLinkEmbed });

	const tokenService = useTokenService({
		tenantId: cloudId,
		space: {
			id: spaceId,
		},
		referenceId: contentReferenceId,
		shareToken: undefined,
		contentId: contentId || '',
		dataSecurityPolicyPageExport: dataSecurityPolicyPageExport || null,
		refetch,
		documentType: 'database',
	});

	useEffect(() => {
		tokenService.onTokenChange((newToken) => {
			setToken(newToken);
		});
	}, [tokenService]);

	const bridgeData = useMemo(
		() => ({
			spaceKey,
			contentId,
			cloudId,
			activationId,
			contentReferenceId,
			userId,
			spaceId: spaceId ?? undefined,
			spaceName: spaceName ?? undefined,
			title: title ?? undefined,
			identity: identity ?? undefined,
			token,
		}),
		[
			spaceKey,
			contentId,
			cloudId,
			activationId,
			contentReferenceId,
			userId,
			spaceId,
			spaceName,
			title,
			identity,
			token,
		],
	);

	return { bridgeData, initialUrl };
};
